/* eslint-disable no-undef */

/**
 * ProductGalleryVideo - Carousel-specific video player
 * Extends the base VideoPlayer class with carousel functionality
 */
class ProductGalleryVideo extends HTMLElement {
  constructor () {
    super()
    this.videoPlayer = null
    this.lastTouchTime = 0
  }

  connectedCallback () {
    this.setAttribute('tabindex', '0')

    // Initialize the base video player
    if (window.Tentbox?.VideoPlayer) {
      this.videoPlayer = new Tentbox.VideoPlayer(this)
      this.videoPlayer.initialize()

      // Override mobile behavior for carousel context
      this.setupCarouselSpecificBehavior()
    } else {
      console.error('Tentbox.VideoPlayer not found - make sure global.js is loaded first')
    }

    // Restore state from attributes
    if (this.getAttribute('data-video-loaded') === 'true') {
      this.videoPlayer.videoLoaded = true
    }
    if (this.getAttribute('data-has-been-played') === 'true') {
      this.videoPlayer.hasBeenPlayed = true
    }
    if (this.getAttribute('data-was-paused-by-user') === 'true') {
      this.videoPlayer.wasPausedByUser = true
    }
  }

  disconnectedCallback () {
    if (this.videoPlayer) {
      this.videoPlayer.destroy()
    }
  }

  setupCarouselSpecificBehavior () {
    // Override the video container click handler for carousel-specific touch behavior
    this.removeEventListener('click', this.videoPlayer.handleVideoContainerClick)
    this.addEventListener('click', this.handleCarouselVideoContainerClick.bind(this))
  }

  handleCarouselVideoContainerClick (e) {
    if (e.target.closest('.video-control-btn, .mute-btn, .fullscreen-btn, .progress-bar-container')) {
      return
    }

    if (this.videoPlayer.isMobileOrTablet() && this.videoPlayer.isPlaying) {
      const currentTime = Date.now()
      const timeSinceLastTouch = currentTime - this.lastTouchTime
      this.lastTouchTime = currentTime

      if (!this.classList.contains('controls-hidden') && timeSinceLastTouch > 300) {
        this.classList.add('controls-hidden')
        if (this.videoPlayer.controlsHideTimeout) {
          clearTimeout(this.videoPlayer.controlsHideTimeout)
          this.videoPlayer.controlsHideTimeout = null
        }
      } else {
        this.classList.remove('controls-hidden')
        this.videoPlayer.hideControlsWithDelay()
      }
    }
  }

  // Carousel-specific methods
  pauseForSlideChange () {
    if (!this.videoPlayer) return

    const videoElement = this.querySelector('video')
    if (videoElement && this.videoPlayer.isPlaying && !videoElement.paused) {
      videoElement.pause()
      this.videoPlayer.isPlaying = false
      this.videoPlayer.showPlayState()
      this.videoPlayer.stopProgressUpdate()
    }
  }

  resumeForSlideChange () {
    if (!this.videoPlayer) return

    if (this.videoPlayer.hasBeenPlayed && this.videoPlayer.videoLoaded &&
        !this.videoPlayer.isLoading && !this.videoPlayer.wasPausedByUser) {
      const videoElement = this.querySelector('video')
      if (videoElement && videoElement.paused && videoElement.readyState >= 4) {
        videoElement.play().then(() => {
          this.videoPlayer.isPlaying = true
          this.videoPlayer.showPauseState()
          this.videoPlayer.startProgressUpdate()
        }).catch(() => {
          // It's fine if resume fails, it's not a critical action.
        })
      }
    }
  }

  resetVideo () {
    if (!this.videoPlayer) return

    const videoElement = this.querySelector('video')
    if (videoElement) {
      videoElement.pause()
      videoElement.currentTime = 0
      this.videoPlayer.isPlaying = false
      this.videoPlayer.wasPausedByUser = false
      this.setAttribute('data-was-paused-by-user', 'false')
      this.videoPlayer.showPosterImage()
      this.videoPlayer.showPlayState()
      this.videoPlayer.updateProgressBar(0)
      this.videoPlayer.stopProgressUpdate()

      if (this.videoPlayer.isMobileOrTablet()) {
        if (this.videoPlayer.controlsHideTimeout) {
          clearTimeout(this.videoPlayer.controlsHideTimeout)
          this.videoPlayer.controlsHideTimeout = null
        }
        this.classList.remove('controls-hidden')
      }
    }

    // Auto-resume logic for carousel
    if (this.videoPlayer.hasBeenPlayed && this.videoPlayer.videoLoaded &&
        !this.videoPlayer.isLoading && !this.videoPlayer.wasPausedByUser) {
      const videoElement = this.querySelector('video')
      if (videoElement && videoElement.paused && videoElement.readyState >= 4) {
        videoElement.play().then(() => {
          this.videoPlayer.isPlaying = true
          this.videoPlayer.showPauseState()
          this.videoPlayer.startProgressUpdate()
        }).catch(() => {
          // It's fine if resume fails, it's not a critical action.
        })
      }
    }
  }

  // Presentation mode change handler (Safari fullscreen)
  handlePresentationModeChange () {
    const videoElement = this.querySelector('video')
    if (!videoElement) return

    if (videoElement.webkitPresentationMode === 'inline') {
      this._syncStateAfterFullscreenExit()
    }
  }

  _syncStateAfterFullscreenExit () {
    if (!this.videoPlayer) return

    const videoElement = this.querySelector('video')
    if (videoElement) {
      if (this.videoPlayer.isPlaying && videoElement.paused) {
        this.videoPlayer.isPlaying = false
        this.videoPlayer.showPlayState()
        this.videoPlayer.stopProgressUpdate()
      }
    }
  }

  // Expose base video player methods for external use
  loadVideo () {
    return this.videoPlayer?.loadVideo()
  }

  playVideo () {
    return this.videoPlayer?.playVideo()
  }

  pauseVideo () {
    return this.videoPlayer?.pauseVideo()
  }

  // Getters for compatibility
  get videoLoaded () {
    return this.videoPlayer?.videoLoaded || false
  }

  get isLoading () {
    return this.videoPlayer?.isLoading || false
  }

  get isPlaying () {
    return this.videoPlayer?.isPlaying || false
  }

  get hasBeenPlayed () {
    return this.videoPlayer?.hasBeenPlayed || false
  }

  get wasPausedByUser () {
    return this.videoPlayer?.wasPausedByUser || false
  }

  // State management methods for compatibility
  showPlayState () {
    return this.videoPlayer?.showPlayState()
  }

  showPauseState () {
    return this.videoPlayer?.showPauseState()
  }

  showLoadingState () {
    return this.videoPlayer?.showLoadingState()
  }
}

if (!customElements.get('product-gallery-video')) {
  customElements.define('product-gallery-video', ProductGalleryVideo)
}
